import Core 										from "@/core/Core";
import Utils 										from "@/core/utils/Utils";
import FilesDAO 								from "./FilesDAO";
import FileDescriptor 					from "./FileDescriptor";
import FileChunk 								from "./FileChunk";
import FileOperation 						from "./FileOperation";
import EntityPointer 						from "@/domains/EntityPointer/EntityPointer";
import ContextMenuItem          from "@/lib/components/VContextMenu/ContextMenuItem";
import type {UnwrapNestedRefs} 	from "vue";
/**
 * Сервис для работы с файлами
 * Включает бизнес-логику, гидрацию данных, drag&drop и управление операциями
 */
class FilesService {

	/**
	 * Конфигурация сервиса
	 */
	static config = {
		maxFileSize: 200 * 1024 * 1024,              // 1024MB
		chunkSize: 2 * 1024 * 1024,                  // 2MB chunks
		chunkedUploadEnabled: true,                 // Отключено для первой версии
		// previewableMaxSize: 10 * 1024 * 1024,        // 10MB для предпросмотра
		allowedExtensions: [],                       // пустой массив = все разрешены
		multiple: true,                             // можно ли загружать файлы пачкой
		showUploadConfirmation: true,               // показываем или нет подтверждение загрузки

		// Настройки операций и popup
		// operationsPopupDebounce: 2000,               // 2 секунды до показа popup
		operationsPopupAutoHide: 3000,               // 3 секунды автоскрытие
		operationsCancellableSize: 5 * 1024 * 1024,  // 5MB для кнопки отмены
		// maxConcurrentOperations: 3,                  // максимум одновременных операций
		operationsHistoryLimit: 20,                  // храним последние 20 операций
	};

	/**
	 * Состояние сервиса
	 */
	private static state = reactive({
		isInitialized: false,
		isDragging: false,
		dragCounter: 0,
		operations: new Map<string, UnwrapNestedRefs<FileOperation>>(),
		isPopupVisible: false,
		isPopupMinimized: false,
		isDropZoneDisable: false,
	});

	/**
	 * Инициализация сервиса
	 */
	static async init(): Promise<void> {
		if (this.state.isInitialized) {
			Core.warn('FilesService уже инициализирован');
			return;
		}

		this.createHiddenFileInput();
		this.initDragDropEvents();
		this.state.isInitialized = true;

		//Core.debug('💾 FilesService инициализирован');
	}

	/**
	 * Деинициализация сервиса
	 */
	static destroy(): void {
		if (!this.state.isInitialized) return;

		this.destroyDragDropEvents();
		this.removeHiddenFileInput();
		this.state.isInitialized = false;
	}

	// ========= ОСНОВНЫЕ МЕТОДЫ РАБОТЫ С ФАЙЛАМИ =========


	static async get(
		uid: string,
		options?: { silent?: boolean }
	): Promise<FileDescriptor | null> {
		const response = await FilesDAO.getInfo(uid);

		if (!response.ok) {
			if (!options?.silent) {
				Core.error(`Не удалось загрузить файл ${uid}: ${response.error?.message}`, response.error);
			}
			return null;
		}

		return FileDescriptor.fromServer(response.data);
	}

	/**
	 * Получить список файлов с гидрацией
	 */
	static async getList(parentEntities: EntityPointer[], fileCategoryName : string | null): Promise<FileDescriptor[] | Record<string, FileDescriptor[]> | null> {
		const response = await FilesDAO.getList(parentEntities, fileCategoryName);

		if (!response.ok) {
			return null;
		}

		const rawData = response.data;

		// Гидрируем данные в зависимости от структуры ответа
		if (parentEntities.length === 1) {
			// Для одной сущности - массив файлов
			return rawData.map((fileData: any) => FileDescriptor.fromServer(fileData));
		} else {
			// Для нескольких сущностей - объект с ключами типа "typeName:id"
			const hydratedData: Record<string, FileDescriptor[]> = {};

			for (const [key, filesArray] of Object.entries(rawData)) {
				hydratedData[key] = (filesArray as any[]).map(fileData => FileDescriptor.fromServer(fileData));
			}

			return hydratedData;
		}
	}

	/**
	 * Загрузить файлы через диалог выбора FIXME тут надо будет наверное имя поменять
	 */
	static showUploadFilesDialog(targetEntity: EntityPointer): Promise<FileDescriptor[]> {
		return new Promise<FileDescriptor[]>((resolve, reject) => {
			const input = document.getElementById('global-file-input') as HTMLInputElement;

			// Настраиваем input под текущую задачу
			input.multiple = this.config.multiple;
			input.accept = this.config.allowedExtensions.length > 0 ? this.config.allowedExtensions.join(',') : '';

			input.onchange = async (event) => {
				try {
					const files = Array.from((event.target as HTMLInputElement).files || []);
					const results = await this.uploadMultipleFiles(files, targetEntity);
					resolve(results);
				} catch (error) {
					reject(error);
				} finally {
					input.value = ''; // Очищаем input
				}
			};

			input.click();
		});
	}

	/**
	 * Загрузить несколько файлов
	 */
	static async uploadMultipleFiles(files: File[], targetEntity: EntityPointer): Promise<FileDescriptor[]> {
		if (this.config.showUploadConfirmation) {
			// если включено подтверждение загрузки файлов,
			// то надо показать список файлов и попросить подтвердить
			// есть ощущение что довольно клево должно быть для загрузки множества файлов
			const confirmed = await this.showUploadConfirmation(files, targetEntity);
			if (!confirmed) return [];
		}

		const results: FileDescriptor[] = [];

		for (const file of files) {
			try {
				this.validateFile(file);

				const result = await this.uploadSingleFile(file, targetEntity);
				if (result) {
					results.push(result);
				}
			} catch (error) {
				Core.error(`Ошибка загрузки файла ${file.name}:`, error);
				Core.showNotification.error(`Не удалось загрузить файл ${file.name}: ${(error as Error).message}`);
			}
		}

		// Генерируем событие о завершении загрузки
		if (results.length > 0) {
			Core.eventBus.FilesUploaded.emit({ files: results, entity: targetEntity });
		}

		return results;
	}

	/**
	 * Загрузить один файл с созданием операции
	 */
	static async uploadSingleFile(file: File, entity: EntityPointer): Promise<FileDescriptor | null> {
		const operation = FileOperation.fromUploadRequest(file, entity);

		this.addOperation(operation);
		try {

			const result = await this.uploadFileInternal(file, entity, operation);

			if (!result) {
				// Запрос был отменен
				return null;
			}

			operation.complete();

			Core.showNotification.success(`Файл ${file.name} успешно загружен`); // TODO перенести внутрь операции?

			Core.eventBus.FeedReload.emit();
			return result;

		} catch (error) {
			operation.fail((error as Error).message);
			// Core.showNotification.error(`Ошибка загрузки файла ${file.name}: ${(error as Error).message}`);  // TODO перенести внутрь операции?
			throw error;
		}
	}

	static lockDragDrop(): void {
		this.state.isDropZoneDisable = true;
	}

	static unlockDragDrop(): void {
		this.state.isDropZoneDisable = false;
	}

	static get isDragDropLocked(): boolean {
		return this.state.isDropZoneDisable;
	}


	/**
	 * Универсальный метод загрузки файла (целиком или чанками)
	 */
	private static async uploadFileInternal(file: File, targetEntity: EntityPointer, operation: UnwrapNestedRefs<FileOperation>): Promise<FileDescriptor | null> {
		operation.updateProgress(0);
		if (this.config.chunkedUploadEnabled && file.size > this.config.chunkSize) {
			return this.uploadFileInChunks(file, targetEntity, operation);
		} else {
			return this.uploadFileWhole(file, targetEntity, operation);
		}
	}

	/**
	 * Загрузка файла целиком
	 */
	private static async uploadFileWhole(file: File, targetEntity: EntityPointer, operation: UnwrapNestedRefs<FileOperation>): Promise<FileDescriptor | null> {
		// Конвертация в dataURL
		const dataUrl = await Utils.file.fileToDataURL(file);
		operation.updateProgress(30, Math.floor(file.size * 0.3));

		const uploadData = this.createUploadData(file, targetEntity, dataUrl);

		// Загружаем через DAO
		const response = await FilesDAO.upload(uploadData, {
			signal: operation.abortSignal,
			onProgress: (progress: any) => {
				operation.updateCombinedProgress(30, progress);
			}
		});

		if (response.isCancelled) {
			return null;
		}

		if (!response.ok) {
			throw Error('Не удалось сохранить файл');
		}


		operation.updateProgress(100, file.size);
		return FileDescriptor.fromServer(response.data.fileDescriptor);
	}

	/**
	 * Загрузка файла чанками
	 */
	private static async uploadFileInChunks(file: File, targetEntity: EntityPointer, operation: UnwrapNestedRefs<FileOperation>): Promise<FileDescriptor | null> {
		const chunks = this.splitFileIntoChunks(file);
		const totalChunks = chunks.length;
		const activeChunkControllers = new Set<AbortController>();

		Core.showNotification.default(`Загружаем файл ${file.name} по частям (${totalChunks} частей)`);

		// При отмене основной операции отменяем все чанки
		operation.abortSignal.addEventListener('abort', () => {
			activeChunkControllers.forEach(controller => controller.abort());
			activeChunkControllers.clear();
		});

		let result: FileDescriptor | null = null;

		for (let i = 0; i < chunks.length; i++) {
			const chunk = chunks[i];
			const chunkController = new AbortController();
			activeChunkControllers.add(chunkController);

			try {
				const dataUrl = await Utils.file.fileToDataURL(chunk.blob);

				const uploadData = this.createUploadData(file, targetEntity, dataUrl, {
					chunkNumber: i + 1,
					totalChunks: totalChunks,
					isChunked: true
				});

				const response = await FilesDAO.upload(uploadData, {
					signal: chunkController.signal
				});

				if (response.isCancelled || operation.abortSignal.aborted) {
					return null; // пользователь отменил
				}

				if (!response.ok) {
					throw Error('Не удалось сохранить файл');
				}

				result = FileDescriptor.fromServer(response.data.fileDescriptor);

				// Обновляем прогресс
				const progress = Math.round(((i + 1) / totalChunks) * 100);
				const bytesTransferred = Math.round((file.size * (i + 1)) / totalChunks);
				operation.updateProgress(progress, bytesTransferred);
			} finally {
				activeChunkControllers.delete(chunkController);
			}
		}

		return result;
	}

	/**
	 * Создание данных для загрузки (общий метод)
	 */
	private static createUploadData(file: File, targetEntity: EntityPointer, dataUrl: string, chunkInfo?: {
		chunkNumber: number;
		totalChunks: number;
		isChunked: boolean;
	}) {
		const uploadData = {
			parentEntity: targetEntity,
      /** оставлю пока так, todo: добавить на прочих вызовах */
      overwriteExisting: false,
			file: {
				name: file.name,
				mimeType: file.type,
				size: file.size,
				data: dataUrl,
				chunkInfo: chunkInfo ?? null // Добавляем информацию о чанках если нужно
			},
		};

		return uploadData;
	}

	/**
	 * Скачать файл с созданием операции
	 */
	static async downloadFile(fileDescriptor: FileDescriptor): Promise<void> {
		const operation = FileOperation.fromDownloadRequest(fileDescriptor);
		this.addOperation(operation);

		try {
			operation.updateProgress(0);

			const response = await FilesDAO.download(fileDescriptor.uid, {
				signal: operation.abortSignal,
				onProgress: (progress: any) => {
					operation.updateProgress(progress.percentage, progress.loaded);
				}
			});

			if (response.isCancelled) {
				// Скачивание отменено - просто завершаем без ошибки и уведомлений
				return;
			}

			if (!response.ok) {
				operation.fail(response.error?.message || 'Ошибка скачивания');
				Core.showNotification.error('Ошибка при скачивании файла');
				return;
			}

			const rawData = response.data;

			Utils.file.downloadFromBase64(rawData.data, rawData.name, rawData.mimeType);
			operation.updateProgress(100, fileDescriptor.size);

			fileDescriptor.markAsDownloaded();
			operation.complete();

			Core.showNotification.success('Файл успешно скачан');
			Core.eventBus.FilesDownloaded.emit( { files: [fileDescriptor] });

		} catch (error) {
			operation.fail((error as Error).message);
			Core.error('Ошибка скачивания файла:', error);
			Core.showNotification.error('Ошибка при скачивании файла');
		}
	}

  /**
   * Возвращает содержимое файла как data URL (base64) для подстановки в <img src>.
   * Файл на диск не пишется, операция не создаётся.
   */
  // fixme по идее fileDescriptor не нужен, можно будет убрать потом и запрашивать по uid
  static async getFileAsDataUrl(fileDescriptor: FileDescriptor): Promise<string | null> {
    const response = await FilesDAO.download(fileDescriptor.uid);

    if (!response.ok) {
      Core.error(`Не удалось загрузить файл ${fileDescriptor.uid}:`, response.error);
      return null;
    }

    const rawData = response.data;

    return `data:${rawData.mimeType};base64,${rawData.data}`;
  }

	/**
	 * Предпросмотр файла в новом окне
	 */
	static async previewFile(fileDescriptor: FileDescriptor): Promise<void> {
		if (!fileDescriptor.isPreviewable) {
			Core.showNotification.warning(fileDescriptor.previewDisabledReason || 'Предпросмотр недоступен');
			return;
		}

		const operation = FileOperation.fromDownloadRequest(fileDescriptor);
		this.addOperation(operation);

		try {
			operation.updateProgress(0);

			const response = await FilesDAO.download(fileDescriptor.uid, {
				signal: operation.abortSignal,
				onProgress: (progress: any) => {
					operation.updateProgress(progress.percentage, progress.loaded);
				}
			});

			if (response.isCancelled) {
				// Скачивание отменено - просто завершаем без ошибки и уведомлений
				return;
			}

			if (!response.ok) {
				operation.fail(response.error?.message || 'Ошибка загрузки');
				Core.showNotification.error('Не удалось открыть файл для предпросмотра');
				return;
			}

			const rawData = response.data;

			operation.updateProgress(100, fileDescriptor.size);

			const blob = Utils.file.base64ToBlob(rawData.data, rawData.mimeType);

			if (blob) {
				const objectURL = URL.createObjectURL(blob);
				const previewWindow = window.open(objectURL, '_blank');

				if (!previewWindow) {
					Core.showNotification.warning('Браузер заблокировал открытие окна предпросмотра');
					Utils.file.downloadBlob(blob); // Fallback на скачивание
				} else {
					// Очистка URL после использования
					setTimeout(() => URL.revokeObjectURL(objectURL), 5000);
				}
			}

			operation.complete()
		} catch (error) {
			operation.fail((error as Error).message);

			Core.error('Ошибка предпросмотра файла:', error);
			Core.showNotification.error('Не удалось открыть файл для предпросмотра');
		}
	}

	/**
	 * Удалить файл
	 */
	static async deleteFile(fileDescriptor: FileDescriptor): Promise<boolean> {
		const response = await FilesDAO.delete(fileDescriptor.uid);

    if (response.ok) {
			Core.showNotification.success('Файл успешно удален');
    }

		Core.eventBus.FilesDeleted.emit({ files: [fileDescriptor] });

    return response.ok;
	}

	// ========= УПРАВЛЕНИЕ ОПЕРАЦИЯМИ =========

	static addOperation(operation: UnwrapNestedRefs<FileOperation>): void {
		this.state.operations.set(operation.id, operation);

		// Очищаем избыточные завершенные операции при добавлении новой
		this.cleanupExcessOperations();
	}

	static updateOperation(id: string, updates: Partial<FileOperation>): void {
		const operation = this.state.operations.get(id);
		if (operation) {
			Object.assign(operation, updates);
		}
	}

	static removeOperation(id: string): void {
		this.state.operations.delete(id);
	}

	static cancelOperation(id: string): void {
		const operation = this.state.operations.get(id);
		if (operation && operation.canBeCancelled()) {
			operation.cancel();
			Core.showNotification.info('Операция отменена');
		}
	}

	/**
	 * Очистка избыточных завершенных операций при превышении лимита
	 */
	private static cleanupExcessOperations(): void {
		const completedOps = Array.from(this.state.operations.values())
			.filter(op => op.isCompleted)
			.sort((a, b) => {
				const timeA = a.time.end?.getTime() || 0;
				const timeB = b.time.end?.getTime() || 0;
				return timeB - timeA; // Новые первыми
			});

		if (completedOps.length > this.config.operationsHistoryLimit) {
			const toRemove = completedOps.slice(this.config.operationsHistoryLimit);
			toRemove.forEach(op => this.removeOperation(op.id));
		}
	}

	// ========= DRAG & DROP ЛОГИКА =========

	private static createHiddenFileInput(): void {
		const input = document.createElement('input');
		input.type = 'file';
		input.style.display = 'none';
		input.id = 'global-file-input';

		if (this.config.multiple) {
			input.multiple = true;
		}

		if (this.config.allowedExtensions.length > 0) {
			input.accept = this.config.allowedExtensions.join(',');
		}

		document.body.appendChild(input);
	}

	private static removeHiddenFileInput(): void {
		const input = document.getElementById('global-file-input');
		if (input) {
			input.remove();
		}
	}

	private static initDragDropEvents(): void {
		window.addEventListener("dragenter", this.handleWindowDragEnter);
		window.addEventListener("dragleave", this.handleWindowDragLeave);
		window.addEventListener("dragover", this.handleWindowDragOver);
		window.addEventListener("drop", this.handleWindowDrop);
	}

	private static destroyDragDropEvents(): void {
		window.removeEventListener("dragenter", this.handleWindowDragEnter);
		window.removeEventListener("dragleave", this.handleWindowDragLeave);
		window.removeEventListener("dragover", this.handleWindowDragOver);
		window.removeEventListener("drop", this.handleWindowDrop);
	}

  /**
   * Метод проверяет наличие файлов в перетаскиваемых элементах
   * Вернет true - если файлы есть, false - если файлов нет
   * @param event
   * @private
   */
  private static dragEventHasFiles(event: DragEvent): boolean {
    if (event.dataTransfer === null) return false;
    if (Array.from(event.dataTransfer.types).includes('Files') === false) return false;
    return true;
  }

	private static handleWindowDragEnter = (event: DragEvent) => {
    if (FilesService.dragEventHasFiles(event) === false) return;

		event.preventDefault();
		event.stopPropagation();

		this.state.isDragging = true;
		this.state.dragCounter++;
	};

	private static handleWindowDragOver = (event: DragEvent) => {
    if (FilesService.dragEventHasFiles(event) === false) return;
		event.preventDefault();
		event.stopPropagation();
	};

	private static handleWindowDragLeave = (event: DragEvent) => {
    if (FilesService.dragEventHasFiles(event) === false) return;
		event.preventDefault();
		event.stopPropagation();

		this.state.dragCounter--;
		if (this.state.dragCounter === 0) {
			this.state.isDragging = false;
		}
	};

	private static handleWindowDrop = (event: DragEvent) => {
    if (FilesService.dragEventHasFiles(event) === false) return;
		event.preventDefault();
		event.stopPropagation();

		this.state.isDragging = false;
		this.state.dragCounter = 0;

		if (this.state.isDropZoneDisable) {
			return;
		}

		/** если файл не дотащили до дроп зоны, то вызываем диалог с выбором сущности */
		Core.eventBus.FilesDroppedOutsideZone.emit({
			files: Array.from(event.dataTransfer?.files || []),
		});
	};

	static async handleDrop(event: DragEvent, targetEntity: EntityPointer): Promise<void> {
    if (!FilesService.dragEventHasFiles(event)) return;
		event.preventDefault();
		event.stopPropagation();

		this.state.isDragging = false;
		this.state.dragCounter = 0;

		const files = Array.from(event.dataTransfer?.files || []);
		if (files.length > 0) {
			await this.uploadMultipleFiles(files, targetEntity);
		}
	}

	/** обнуляем drag, чтобы при закрытии FilesSelectUploadDialog DropZone в FilesList снова работала  */

	static resetDragState(): void {
		this.state.isDragging = false;
		this.state.dragCounter = 0;
	}


	// ========= ВСПОМОГАТЕЛЬНЫЕ МЕТОДЫ =========

	private static showUploadConfirmation(files: File[], targetEntity: EntityPointer): Promise<boolean> {

		return new Promise((resolve) => {

			Core.dialogs.VFilesUploadConfirmationDialog.show(
				{
					uploadFiles: files,
					targetEntity
				}, {
					approve: () => {
						resolve(true)
					},
					cancel: () => {
						resolve(false)
					}
				}
			)
		})

	}

	private static splitFileIntoChunks(file: File): FileChunk[] {
		const chunks: FileChunk[] = [];
		const chunkSize = this.config.chunkSize;

		for (let start = 0; start < file.size; start += chunkSize) {
			const end = Math.min(start + chunkSize, file.size);
			const blob = file.slice(start, end);

			chunks.push(new FileChunk(blob, start, end, chunks.length + 1));
		}

		return chunks;
	}

	private static validateFile(file: File): void {
		if (file.size > this.config.maxFileSize) {
			throw new Error(`Файл ${file.name} превышает максимальный размер ${Utils.file.formatSize(this.config.maxFileSize)}`);
		}

		if (this.config.allowedExtensions.length > 0) {
			const extension = '.' + file.name.split('.').pop()!.toLowerCase();
			if (!this.config.allowedExtensions.includes(extension)) {
				throw new Error(`Расширение файла ${extension} не разрешено`);
			}
		}
	}

	// ========= ГЕТТЕРЫ ДЛЯ СОСТОЯНИЯ =========

	static get isInitialized(): boolean {
		return this.state.isInitialized;
	}

	static get isDragging(): boolean {
		return this.state.isDragging;
	}

	static get operations(): Map<string, FileOperation> {
		return this.state.operations;
	}

	static getDownloadOperation(fileDescriptor: FileDescriptor): UnwrapNestedRefs<FileOperation> | undefined {
		const id = FileOperation.getDownloadIdByFileDescriptor(fileDescriptor)
		return this.state.operations.get(id);
	}

  /**
   * Контекстное меню группы — шапка сущности в гриде файлов.
   * row — строка-группа FilesGridRow: { id, data: EntityPointer, children: FileDescriptor[] }.
   * Показывается всегда: и в single-, и в multi-режиме.
   */
  static get groupContextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Загрузить файл',
        'upload',
        (row: any) => {
          return FilesService.showUploadFilesDialog(row.data);
        },
        null,
        false,
        true,
        true,
      ),

      new ContextMenuItem(
        'Скачать всё',
        'download',
        async (row: any) => {
          const confirmed = await Core.dialogs.confirm(
            'Вы уверены, что хотите скачать все файлы?',
            row.data.name,
          );

          if (confirmed) {
            row.children.forEach((file: FileDescriptor) => {
              FilesService.downloadFile(file);
            });
          }
        },
        (row: any) => {
          if (Core.utils.isEmptyArray(row.children)) {
            return 'Нет файлов для скачивания';
          } else {
            return null;
          }
        },
        false,
        false,
        false,
      ),
    ];
  }

	static get contextMenu(): ContextMenuItem[] {
		return [
			new ContextMenuItem(
				'Скачать',
				'download',
				(file: File) => { return FilesService.downloadFile(file); },
				undefined,
			),
			new ContextMenuItem(
				'Предпросмотр',
				'visibility',
				async (file: File) => {
					if (!file.isPreviewable) return;
					/** После превью нужно показать, что он уже скачен */
					return await FilesService.previewFile(file);
				 },
				undefined,
				false,  // hidden
				true,  // refreshAfter
				true,   // dividerAfter
			),
			new ContextMenuItem(
				'Удалить',
				'delete',
				async (file: File) => {
					if (await Core.confirm( `Файл: ${file.name}`, 'Удалить файл?')) {
						await FilesService.deleteFile(file);

						Core.eventBus.FeedReload.emit();
					}
				},
				undefined,
				false,
				true
			),
		];
	}
}

export default FilesService;
